import { first, debounceTime } from 'rxjs/operators';
import { Component, OnInit, Input, Output, EventEmitter, NgZone } from "@angular/core";
import { FormControl, AbstractControl } from "@angular/forms";
import { Observable } from "rxjs";

import { ContactService } from "../../../admin/services/contacts.service";
import { GroupService } from "../../../admin/services/group.service";
import { WorkOrderService } from "../../../common/services/work-order.service";

import { Contact } from "../../../admin/models/contact.model";
import { FlowInstance } from "../../models/flow.model";
import { genId } from "../../../core/services/genId";
import { Group, GroupStub } from "../../../admin/models/group.model";
import { WorkOrderWithAssignments, WorkOrderStatus, WorkOrderType } from "../../../common/models/work-order.model";
import { AuthService } from '../../../services/auth.service';
import { LoaderService } from '../../../common/services/loader.service';
import { DynamicDialogService } from '../../../core/services/dynamic-dialog.service';
import { Dialog, DialogButton } from '../../../core/models/dialog.model';

@Component({
    selector: "add-ad-hoc-reviewer",
    templateUrl: "add-ad-hoc-reviewer.component.html",
    styleUrls: ["../flow/flow.component.scss", './add-ad-hoc-reviewer.component.scss'],
    standalone: false
})

export class AddAdHocReviewerComponent implements OnInit {

  @Input() public workflow: FlowInstance = null;
  @Input() public workOrderId: string;
    @Input() public applicationNumber: string = null;
  @Input() public workOrderDescription: string = null;
  @Input() public workOrderTargetUrlPrefix: string = null;
  @Input() public workOrderOrgId: string = null;
  @Input() public workOrderOrgName: string = null;
  @Output() public closeEvent = new EventEmitter<boolean>();
  public otherGroup: GroupStub = {
    id: "specificUser",
    displayName: "Select a User"
  };
  public searchControl: AbstractControl = new FormControl("");
  public reviewGroupId: string;
  public reviewGroups: GroupStub[] = [];
  public searchResults = new Array<Contact>();
  public userPool = new Array<Contact>();
  public showUserSearch: boolean = false;
  public showSearchResults: boolean = false;
  public userToAdd: Contact | null = null;
  public disableAddUser = true;
  public selectedUsers = new Array<Contact>();
  public selectedGroups = new Array<GroupStub>();
  readonly notifySingleText: string = "You have assigned this action to {user}.  A notification message will be sent alerting them of action to be taken.";
  readonly notifyMultipleText: string = "You have assigned this action to the following users: {users}.  Notification messages will be sent alerting them of the actions to be taken.";
  readonly notifyGroupText: string = "You have assigned this action to the following groups: {groups}.  Notification messages will be sent alerting them of the actions to be taken.";

  applicationSubmittedDialog: Dialog = new Dialog("", [new DialogButton("OK", true, () => this.onDialogClose())]);

  constructor(
    public readonly contacts: ContactService,
    public readonly loader: LoaderService,
    public readonly groups: GroupService,
    public readonly workers: WorkOrderService,
    public readonly dialogService: DynamicDialogService,
    public readonly authService: AuthService,
    public _ngZone: NgZone
  ) {
    this.searchControl.valueChanges.pipe(debounceTime(100)).subscribe((result) => {
      if (!this.userToAdd && result != null)
        this.searchUsers(result);
    });
  }

  ngOnInit() {
        this.loader.toggleLoader(true);
    (this.groups.get({ stubs: true, internal: true }) as Observable<GroupStub[]>).pipe(
      first())
      .subscribe(groups => {
          this.reviewGroups = groups.concat([this.otherGroup]);
          this.loader.toggleLoader(false);
      });
    (this.contacts.getAll<Contact[]>({ internal: true })).pipe(
      first())
      .subscribe(users => {
          this.userPool = users;
          this.loader.toggleLoader(false);
      });
  }

  public notify(message: string): void {
    this.applicationSubmittedDialog.message = message;
    this.applicationSubmittedDialog.toggleDialog(true);
    this.dialogService.updateDialog(this.applicationSubmittedDialog);
  }

  public groupChanged(): void {
    const reviewGroup = this.reviewGroups.find(g => g.id === this.reviewGroupId);
      if (this.reviewGroupId === "specificUser") {
          //Users haven't finished loading yet toggle loader
          if (!this.userPool || this.userPool.length == 0)
              this.loader.toggleLoader(true);
      this.showUserSearch = true;
    } else if (reviewGroup) {
      this.showUserSearch = false;
      const existing = this.selectedGroups.find(g => g.id === reviewGroup.id);

      if (!existing)
        this.selectedGroups.push(reviewGroup);
    } else
      this.showUserSearch = false;
  }

  public searchUsers(search: string): void {
    this.searchResults = new Array<Contact>();
    search = search.toLocaleLowerCase();

    this._ngZone.runOutsideAngular(() => {
      for (let i = 0; i < this.userPool.length; i++) {
        const user = this.userPool[i];

        if (user.firstName.toLocaleLowerCase().indexOf(search) !== -1 || user.lastName.toLocaleLowerCase().indexOf(search) !== -1)
          this.searchResults.push(user);
      }

      this._ngZone.run(() => {
        this.showSearchResults = search.length !== 0;
      });
    });
  }

  public addUser(): void {
    this._ngZone.runOutsideAngular(() => {
      if (this.userToAdd !== null && this.selectedUsers.findIndex(x => x === this.userToAdd) === -1)
        this.selectedUsers.push(this.userToAdd);
      this.userPool.splice(this.userPool.findIndex(x => x === this.userToAdd), 1);
    });
    this.searchControl.reset();
    this.userToAdd = null;
    this.showSearchResults = false;
    this.disableAddUser = true;
  }

  public selectUser(user: Contact): void {
    this.searchControl.setValue(`${user.firstName} ${user.lastName}`);
    this.showSearchResults = false;
    this.disableAddUser = false;
    this.userToAdd = user;
  }

  public removeUser(index: number): void {
    let user = this.selectedUsers.splice(index, 1)[0];
    this.userPool.push(user);
  }

  public removeGroup(index: number): void {
    this.selectedGroups.splice(index, 1);
  }

  public cancel(): void {
    this.resetAll();
    this.closeEvent.emit(false);
  }

    public assign(): void {

    this.loader.toggleLoader(true);
    const workOrders = new Array<WorkOrderWithAssignments>();

    //If a workflow has been provided, and workOrderOrgId and workOrderOrgName have not, try to pull them from workflow
    if (this.workflow) {
        if (this.workOrderDescription == null)
            this.workOrderDescription = `Ad hoc Review of Workflow ${this.workflow.id}`;
        //an attempted workaround for the registration flows where there is no organization yet created
        if (!this.workflow.organizationId) {
            //if you're adding an ad hoc item this way, you've already coming from a pre-existing workitem tied to the flow
            //attempt to find any existing workorders with the fields populated to carry over (these would have been set via the general addreviewitem sideeffect and pulled from context)
            const existingWorkItemToCopyFrom = this.workflow.associatedWorkItems.find(x => (x.organizationId != null && x.organizationId != "") || (x.companyName != null && x.companyName != ""));
            if (existingWorkItemToCopyFrom) {
                if (this.workOrderOrgId == null)
                    this.workOrderOrgId = existingWorkItemToCopyFrom.organizationId;
                if (this.workOrderOrgName == null)
                    this.workOrderOrgName = existingWorkItemToCopyFrom.organizationName;
            }
        }
        else {
            if (this.workOrderOrgId == null)
                this.workOrderOrgId = this.workflow.organizationId
        }
    }

    for (let i = 0; i < this.selectedUsers.length; i++) {
      const workOrderId = genId();
      let targetUrlPrefix = this.workOrderTargetUrlPrefix;
      if (targetUrlPrefix == null)
          targetUrlPrefix = `/flow/${this.workflow.id}`;

      const work = {
        id: workOrderId,
        applicationNumber: this.applicationNumber,
        title: "Ad hoc Review",
        description: this.workOrderDescription,
        transition: "",
        flowInstanceId: this.workflow ? this.workflow.id : null,
        status: WorkOrderStatus.Pending,
        type: WorkOrderType.AdHocReview,
        assignedUserId: this.selectedUsers[i].id,
        creatorId: this.authService.User.id,
        targetUrl: targetUrlPrefix + `/${workOrderId}`,
        assignee: null,
        creator: null,
        assignedGroup: null,
        organizationId: this.workOrderOrgId,
        companyName: this.workOrderOrgName,
      } as WorkOrderWithAssignments;
      workOrders.push(work);
    }

    for (let i = 0; i < this.selectedGroups.length; i++) {
    const workOrderId = genId();
    let targetUrlPrefix = this.workOrderTargetUrlPrefix;
    if (targetUrlPrefix == null)
        targetUrlPrefix = `/flow/${this.workflow.id}`;
      const work = {
        id: workOrderId,
        applicationNumber: this.applicationNumber,
        title: "Ad hoc Review",
        description: this.workOrderDescription,
        transition: "",
        flowInstanceId: this.workflow ? this.workflow.id : null,
        status: WorkOrderStatus.Pending,
        type: WorkOrderType.AdHocReview,
        assignedGroupId: this.selectedGroups[i].id,
        creatorId: this.authService.User.id,
        targetUrl: targetUrlPrefix + `/${workOrderId}`,
        assignee: null,
        creator: null,
        assignedGroup: null,
        organizationId: this.workOrderOrgId,
        companyName: this.workOrderOrgName,
      } as WorkOrderWithAssignments;
      workOrders.push(work);
    }

    this.workers.addWorkOrders({ workOrderId: this.workOrderId, workOrders: workOrders }).pipe(first()).subscribe((result) => {
      let nameList: string = "";
      for (let m = 0; m < this.selectedUsers.length; m++) {
        const user = this.selectedUsers[m];
        if (m > 0) {
          nameList = `${nameList},`;
        }
        nameList = `${nameList} ${user.firstName} ${user.lastName}`;
      }
      let groupList: string = "";
      for (let g = 0; g < this.selectedGroups.length; g++) {
        const group = this.selectedGroups[g];
        if (g > 0) {
          groupList = `${groupList},`;
        }
        groupList = `${groupList} ${group.displayName}`;
        }
        //If a workflow was provided, associate the work order(s) to it
        if (this.workflow) {
            for (let r = 0; r < result.length; r++) {
                this.workflow.associatedWorkItems.push(result[r] as WorkOrderWithAssignments);
            }
        }
      let message = "";
      if (this.selectedUsers.length === 1) {
        message = this.notifySingleText.replace("{user}", nameList);
      } else if (this.selectedUsers.length > 1) {
        message = this.notifyMultipleText.replace("{users}", nameList);
      }
      if (this.selectedGroups.length > 0) {
        const groupMessage = this.notifyGroupText.replace("{groups}", groupList);
        message = `${message} ${groupMessage}`;
      }
      if (message.length > 0) {
        this.notify(message);
      }
      else
        this.closeEvent.emit(false);

      this.resetAll();
      this.loader.toggleLoader(false);
    }, (error) => {
          console.log(error);
          this.loader.toggleLoader(false);
    });
  }

  public resetAll(): void {
    this.searchResults = new Array<Contact>();
    this.userToAdd = null;
    this.showSearchResults = false;
    this.showUserSearch = false;
    this.searchControl.setValue('');
    this.selectedUsers = new Array<Contact>();
    this.selectedGroups = new Array<Group>();
    this.reviewGroupId = "";
  }

    public onDialogClose(): void {
        this.closeEvent.emit(false);
  }

}
